import { DocumentNode, useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { MutableRefObject, useContext, useState } from 'react';
import { handleComponentQueryError, useComponentQueryErrorHandling } from './parseErrorResponse';
import UserContext from '../components/Context/UserContext';
import CreateSpecialOrderProductMutation from '../graphql/mutations/product/CreateSpecialOrderProduct.graphql';
import { serviceActions } from '../state/modules/service';
import CreateProductFromLuckinsItemMutation from '../graphql/mutations/product/CreateProductFromLuckinsItem.graphql';
import getClientMutationId from './getClientMutationId';
import { useSearchVariables } from '../components/connectedForm/MultiQueryDropdown';
import ProductTradeCounterSearchQuery from '../graphql/queries/product/ProductTradeCounterSearchQuery.graphql';
import flattenEdges from './flattenEdges';
import LuckinsItem from '../graphql/entities/product/LuckinsItem';
import { useCompany } from './FetchConfig';
import RaiseReturnOrderFromOrderLines from 'graphql/mutations/sales/RaiseReturnOrderFromSalesOrderLines.graphql';
import RaiseReturnOrderFromInvoiceLines from 'graphql/mutations/salesLedger/RaiseReturnOrderFromSalesInvoiceLines.graphql';
import normalizeVariables, { normalizeFields } from '../graphql/schema/normalizeVariables';
import SalesOrderContext from '../components/Context/SalesOrderContext';
import SalesInvoiceContext, { SalesInvoiceContextType } from '../components/Context/SalesInvoiceContext';
import { SalesInvoice, SalesOrder } from '../graphql/entities';
import {
  AlertType,
  BooleanOperator,
  FilterOperation,
  ICreateProductFromLuckinsItemMutationResult,
  ICreateProductFromLuckinsItemMutationVariables,
  ICreateSpecialOrderProductMutationResult,
  ICreateSpecialOrderProductMutationVariables,
  IFilterInput,
  ILuckinsItemDropdownQueryResult,
  ILuckinsItemDropdownQueryVariables,
  IProduct,
  IProductTradeCounterSearchQueryResult,
  IProductTradeCounterSearchQueryVariables,
  ISalesInvoiceLine,
  ProductClass,
} from '../graphql/schema/p4types';
import { EntityType } from '../@types/entityTypes';
import { IActionProps } from './useActionProps';
import { useP4ToastController } from '../views/system/NotificationsArea';

export type ISpecialOrderProductState = {
  modalOpen: boolean;
  productUuid?: string;
  rowIdx?: number;
};

/**
 * Extracted handling of special order product state & mutation for re-use in trade counter form.
 * Note: The actual modal needs to be defined outside of the hook, just it's state is driven from here.
 */
export const useSpecialOrderProductState = () => {
  const dispatch = useDispatch();
  const [specialOrderProductState, setSpecialOrderProductState] = useState<ISpecialOrderProductState>({
    modalOpen: false,
  });
  const handleQueryError = useComponentQueryErrorHandling();

  const {
    currentUser: {
      companyUser: { allowSpecialOrderProducts },
    },
  } = useContext(UserContext);

  const [createSpecialOrderProductMutation] = useMutation<
    ICreateSpecialOrderProductMutationResult,
    ICreateSpecialOrderProductMutationVariables
  >(CreateSpecialOrderProductMutation, {
    onError: handleQueryError,
  });

  const createSpecialOrderProduct: (templateId: number, rowIdx: number) => void = (templateId, rowIdx) => {
    if (!allowSpecialOrderProducts) {
      dispatch(
        serviceActions.genericAlert(
          'Special Order Products',
          'Not allowed - User does not have permission to create special order products.',
          AlertType.Warning
        )
      );
      return;
    }

    if (!!specialOrderProductState.productUuid) {
      console.warn("Attempted to refresh special order product but there's already one being edited.");
      return;
    }

    createSpecialOrderProductMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          templateId: templateId,
        },
      },
    }).then((data) => {
      const productUuid = data?.data?.createSpecialOrderProduct?.result?.uuid;
      if (productUuid) {
        setSpecialOrderProductState({ modalOpen: true, productUuid: productUuid, rowIdx: rowIdx });
      }
    });
  };

  return {
    specialOrderProductState,
    setSpecialOrderProductState,
    createSpecialOrderProduct,
  };
};

/**
 * Create from above special order handling hook to reproduce similar behaviour for Luckins Items => Products.
 */
export const useProductFromLuckinsItemState = () => {
  const [productFromLuckinsItemState, setProductFromLuckinsItemState] = useState<ISpecialOrderProductState>({
    modalOpen: false,
  });
  const handleQueryError = useComponentQueryErrorHandling();

  const [createProductFromLuckinsItemMutation] = useMutation<
    ICreateProductFromLuckinsItemMutationResult,
    ICreateProductFromLuckinsItemMutationVariables
  >(CreateProductFromLuckinsItemMutation, {
    onError: handleQueryError,
  });

  const createProductFromLuckinsItem = (luckinsItemId: number, rowIdx: number) => {
    //Check/warn if not permitted (Not yet implemented/discussed).

    if (!!productFromLuckinsItemState.productUuid) {
      console.warn("Attempted to refresh luckins product but there's already one being edited.");
      return;
    }

    createProductFromLuckinsItemMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          luckinsItemId: luckinsItemId,
        },
      },
    }).then((data) => {
      const productUuid = data?.data?.createProductFromLuckinsItem?.result?.uuid;
      if (productUuid) {
        setProductFromLuckinsItemState({ modalOpen: true, productUuid: productUuid, rowIdx: rowIdx });
      }
    });
  };

  return {
    productFromLuckinsItemState,
    setProductFromLuckinsItemState,
    createProductFromLuckinsItem,
  };
};

/**
 * Sets up and returns the nested filters needed to filter products on whether they are in stock, for trade counter search
 */
const getStockFilter = (warehouseId: number) => {
  const kitOrAssemblyFilter = {
    filterParameters: {
      booleanOperator: BooleanOperator.Or,
      filters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'productClass',
            value: ProductClass.Kit,
          },
        },
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'productClass',
            value: ProductClass.Assembly,
          },
        },
      ],
    },
  };

  const standardProductFilter = {
    filterOperation: FilterOperation.Equals,
    filterParameters: {
      field: 'productClass',
      value: ProductClass.Standard,
    },
  };

  const freeTypeOrProcessFilter = {
    filterParameters: {
      booleanOperator: BooleanOperator.Or,
      filters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'productClass',
            value: ProductClass.FreeType,
          },
        },
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'productClass',
            value: ProductClass.Process,
          },
        },
      ],
    },
  };

  const freeStockFilter = {
    filterParameters: {
      filters: [
        {
          filterOperation: FilterOperation.GreaterThan,
          filterParameters: {
            field: 'productWarehouseDetails.freeStock',
            value: '0',
          },
        },
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'productWarehouseDetails.warehouse.id',
            value: `${warehouseId}`,
          },
        },
      ],
    },
  };

  const makeableFilter = {
    filterParameters: {
      filters: [
        {
          filterOperation: FilterOperation.GreaterThan,
          filterParameters: {
            field: 'productWarehouseDetails.makeable',
            value: '0',
          },
        },
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'productWarehouseDetails.warehouse.id',
            value: `${warehouseId}`,
          },
        },
      ],
    },
  };

  const freeStockOrMakeableFilter = {
    filterParameters: {
      booleanOperator: BooleanOperator.Or,
      filters: [freeStockFilter, makeableFilter],
    },
  };

  return {
    filterParameters: {
      booleanOperator: BooleanOperator.Or,
      filters: [
        {
          filterParameters: {
            booleanOperator: BooleanOperator.And,
            filters: [kitOrAssemblyFilter, freeStockOrMakeableFilter],
          },
        },
        {
          filterParameters: {
            booleanOperator: BooleanOperator.And,
            filters: [standardProductFilter, freeStockFilter],
          },
        },
        freeTypeOrProcessFilter,
      ],
    },
  };
};

export const useProductTradeCounterSearch = ({
  productSearchString,
  setProductSearchString,
  lastSearchWasScan,
  selectProduct,
  warehouseId,
  includeOutOfStock,
}: {
  productSearchString: string;
  setProductSearchString: (newValue: string) => void;
  lastSearchWasScan: MutableRefObject<boolean>;
  selectProduct: (product: IProduct) => void;
  warehouseId: number;
  includeOutOfStock: boolean;
}) => {
  const { dispatchSimpleToast } = useP4ToastController();

  //Attach custom template filter so we can access special order products from trade counter.
  const predefinedFilters: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Custom,
      filterParameters: {
        field: 'template',
      },
    },
  ];

  if (!includeOutOfStock) {
    predefinedFilters.push(getStockFilter(warehouseId));
  }

  const getProductSearchVariables = useSearchVariables(predefinedFilters) as unknown as (
    searchString: string
  ) => IProductTradeCounterSearchQueryVariables;

  const {
    data: productData,
    fetchMore,
    refetch,
  } = useQuery<IProductTradeCounterSearchQueryResult, IProductTradeCounterSearchQueryVariables>(
    ProductTradeCounterSearchQuery,
    {
      skip: productSearchString === '',
      variables: getProductSearchVariables(productSearchString),
      fetchPolicy: 'network-only',
      onCompleted: (response) => {
        if (lastSearchWasScan.current) {
          let newProducts: Array<IProduct> = [];
          if (response.list) {
            newProducts = flattenEdges(response.list.edges ?? []);
          }

          if (newProducts.length === 0) {
            const alertText = `Product with search criteria ${productSearchString} not found`;
            dispatchSimpleToast('Product not found', alertText, AlertType.Warning);
          } else {
            selectProduct(newProducts[0]);
          }

          lastSearchWasScan.current = false;
          setProductSearchString('');
        }
      },
    }
  );

  const loadMore = () => {
    fetchMore({
      variables: {
        ...getProductSearchVariables(productSearchString),
        after: productData?.list?.edges?.length,
      },
    });
  };

  return {
    list: (productSearchString !== '' && productData?.list?.edges?.map((edge) => edge?.node)) || [],
    totalCount: productData?.list?.genericCount,
    loadMore,
    refetch,
  };
};

export const useLuckinsProductsSearch = (productSearchString: string) => {
  const company = useCompany();
  const getLuckinsSearchVariables = useSearchVariables([
    {
      filterOperation: FilterOperation.NotEmpty,
      filterParameters: {
        field: 'products',
      },
    },
  ]) as unknown as (searchString: string) => ILuckinsItemDropdownQueryVariables;
  const { data: luckinsData, fetchMore } = useQuery<
    ILuckinsItemDropdownQueryResult,
    ILuckinsItemDropdownQueryVariables
  >(LuckinsItem.list, {
    skip: productSearchString === '' || !company?.luckinsEnabled,
    variables: getLuckinsSearchVariables(productSearchString),
  });

  const loadMore = () => {
    if (!company?.luckinsEnabled) return;
    fetchMore({
      variables: {
        ...getLuckinsSearchVariables(productSearchString),
        after: luckinsData?.list?.edges?.length,
      },
    });
  };

  return {
    list: luckinsData?.list?.edges?.map((edge) => edge?.node) ?? [],
    totalCount: luckinsData?.list?.genericCount,
    loadMore,
  };
};

const useReturnOrderMutation = ({
  setReturnOrderModalState,
  entity,
  mutation,
  mutationResultName,
}: {
  setReturnOrderModalState: SalesOrderContextType['setReturnOrderModalState'];
  entity: EntityType;
  mutation: DocumentNode;
  mutationResultName?: string;
}) => {
  const [raiseReturnOrder] = useMutation(mutation, {
    onError: (data) => handleComponentQueryError(data, { dispatch, entity, name: 'Could not raise return' }),
  });
  const dispatch = useDispatch();

  return {
    raiseReturn: ({
      filters = [],
      include = true,
      originLines = [],
    }: IActionProps<{ originLines: Array<ISalesInvoiceLine> }>) => {
      if (include && (!originLines || originLines.length === 0)) {
        dispatch(serviceActions.genericAlert('Invalid action', 'Must select one or more lines.'));
        return;
      }

      raiseReturnOrder({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            filters,
            originLines: normalizeVariables(originLines, [...normalizeFields, 'lineCost', 'haggleLineCost']),
            include,
          },
        },
      }).then((data) => {
        const salesCredit = data?.data?.[mutationResultName!]?.result;
        if (salesCredit?.uuid) {
          setReturnOrderModalState({ modalOpen: true, uuid: salesCredit.uuid, initialValues: salesCredit });
        }
      });
    },
  };
};

export const useReturnOrderFromOrderMutation = () => {
  const { setReturnOrderModalState } = useContext(SalesOrderContext);

  return useReturnOrderMutation({
    setReturnOrderModalState,
    entity: SalesOrder,
    mutation: RaiseReturnOrderFromOrderLines,
    mutationResultName: 'raiseReturnOrderFromOrderLines',
  });
};

export const useReturnOrderFromInvoiceMutation = () => {
  const { setReturnOrderModalState } = useContext(SalesInvoiceContext);

  return useReturnOrderMutation({
    setReturnOrderModalState,
    entity: SalesInvoice,
    mutation: RaiseReturnOrderFromInvoiceLines,
    mutationResultName: 'raiseReturnOrderFromInvoiceLines',
  });
};
